import {
  DownloadDocumentDTO,
  ReadSignedUrlDto,
  WorkOrderDto,
  WorkOrderSearchResponseDtoV2,
  WorkOrderSort,
  WorkOrderSortColumnEnum,
  WorkOrderSortDirectionEnum,
  WorkOrderSummaryDto,
} from '@inosio/payments-sdk';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ErrorType, RootState } from '../../../app/store';
import { IsoDateRange } from '../../shared/CalendarModal.V2';

export enum ExtendedWorkOrderStatusEnum {
  NeedsReview = 'Needs Review',
  GroupedInProgress = 'Grouped In Progress',
  OwnerDiscussion = 'Owner Discussion',
  VendorDiscussion = 'Vendor Discussion',
  SentToVendor = 'Sent to Vendor',
  WorkInProgress = 'Work in Progress',
  ObtainingQuotes = 'Obtaining Quotes',
  GroupedCompleted = 'Grouped Completed',
  Completed = 'Completed',
  InvoiceReceived = 'Invoice Received',
  Closed = 'Closed',
}

interface IWorkOrderDataPage {
  Rows: Array<WorkOrderSummaryDto>;
}

export interface IWorkOrderSearchFilters {
  searchDateRange: IsoDateRange | undefined;
  recordStartIndex: number;
  rangeSize: number;
  searchValue: string;
  searchField: string;
  currentPage: number;
  selectedStatusOptions: ExtendedWorkOrderStatusEnum[];
  assigneeUuids: string[];
  sort: WorkOrderSort;
  Title: string;
  WorkOrderId: string;
  PropertyName: string;
  ResidentName: string;
}

type WorkOrderState = {
  // Work Order Filter State
  workOrderSearchFilters: IWorkOrderSearchFilters;
  //searching for work orders
  isFetchingWorkOrders: boolean;
  isFetchingWorkOrdersFailed: boolean;
  isFetchingWorkOrdersSuccess: boolean;
  WorkOrdersFetchResponseDto: WorkOrderSearchResponseDtoV2;
  WorkOrdersDataPages: Array<IWorkOrderDataPage>;
  WorkOrdersFetchError: ErrorType;

  //get a specific work order
  isGettingWorkOrder: boolean;
  isGettingWorkOrderSuccess: boolean;
  isGettingWorkOrderfailed: boolean;
  workOrderDetailDto: WorkOrderDto;

  //fetching details of the maintenance request associated with a workorder
  isCreatingWorkOrder: boolean;
  isCreatingWorkOrderFailed: boolean;
  isCreatingWorkOrderSuccess: boolean;
  CreateWorkOrderError: ErrorType;

  //FetchingBase64 images associated with a maintenance request
  isFetchingBase64Images: boolean;
  isFetchingBase64ImagesFailed: boolean;
  isFetchingBase64ImagesSuccess: boolean;
  ImagesDownloadError: ErrorType;
  WorkOrderImagesData: DownloadDocumentDTO[];

  //Fetching videos associated with a maintenance request
  isFetchingVideos: boolean;
  isFetchingVideosFailed: boolean;
  isFetchingVideosSuccess: boolean;
  MaintenanceVideo: ReadSignedUrlDto;
  VideosDownloadError: ErrorType;

  //downloading Maintenance files (pdf, images etc) to local directory
  isDownloadingWorkOrderFiles: boolean;
  isDownloadingWorkOrderFilesFailed: boolean;
  isDownloadingWorkOrderFilesSuccess: boolean;
  DownloadingWorkOrderFilesError: ErrorType;

  //updating details (like title or description) of a workorder
  isUpdatingWorkOrderDetails: boolean;
  isUpdatingWorkOrderDetailsFailed: boolean;
  isUpdatingWorkOrderDetailsSuccess: boolean;
  UpdatingWorkOrderDetailsError: ErrorType;

  //sending work order email
  isSendingWorkOrderEmail: boolean;
  isSendingWorkOrderEmailFailed: boolean;
  isSendingWorkOrderEmailSuccess: boolean;
  SendingWorkOrderEmailError: ErrorType;

  //adding a workorder note
  isAddingWorkOrderNote: boolean;
  isAddingWorkOrderNoteFailed: boolean;
  isAddingWorkOrderNoteSuccess: boolean;
  AddingWorkOrderNoteError: ErrorType;

  //Deleting a workorder note
  isDeletingWorkOrderNote: boolean;
  isDeletingWorkOrderNoteFailed: boolean;
  isDeletingWorkOrderNoteSuccess: boolean;
  DeletingWorkOrderNoteError: ErrorType;

  //Exporting a work order
  isExportingWorkOrder: boolean;
  isExportingWorkOrderSuccess: boolean;
  isExportingWorkOrderFailed: boolean;
  ExportingWorkOrderError: ErrorType;
};

const initialWorkOrderSearchFilters: IWorkOrderSearchFilters = {
  searchDateRange: undefined,
  recordStartIndex: 0,
  rangeSize: 100,
  searchValue: '',
  searchField: '',
  currentPage: 0,
  selectedStatusOptions: [ExtendedWorkOrderStatusEnum.NeedsReview],
  assigneeUuids: [],
  sort: {
    Column: WorkOrderSortColumnEnum.CreatedDate,
    Direction: WorkOrderSortDirectionEnum.Desc,
  },
  Title: '',
  WorkOrderId: '',
  PropertyName: '',
  ResidentName: '',
};
const getInitialState = () => {
  return {
    workOrderSearchFilters: initialWorkOrderSearchFilters,
    //fetching all maintenance requests
    isFetchingWorkOrders: false,
    isFetchingWorkOrdersFailed: false,
    isFetchingWorkOrdersSuccess: false,
    WorkOrdersDataPages: [],
    WorkOrdersFetchResponseDto: {} as WorkOrderSearchResponseDtoV2,
    WorkOrdersFetchError: {} as ErrorType,

    //get a specific work order
    isGettingWorkOrder: false,
    isGettingWorkOrderSuccess: false,
    isGettingWorkOrderfailed: false,
    workOrderDetailDto: {} as WorkOrderDto,

    //fetching details of the maintenance request associated with a workorder
    isCreatingWorkOrder: false,
    isCreatingWorkOrderFailed: false,
    isCreatingWorkOrderSuccess: false,
    CreateWorkOrderError: {} as ErrorType,

    //FetchingBase64 images associated with a maintenance request
    isFetchingBase64Images: false,
    isFetchingBase64ImagesFailed: false,
    isFetchingBase64ImagesSuccess: false,
    ImagesDownloadError: {} as ErrorType,
    WorkOrderImagesData: [],

    //Fetching videos associated with a maintenance request
    isFetchingVideos: false,
    isFetchingVideosFailed: false,
    isFetchingVideosSuccess: false,
    MaintenanceVideo: {} as ReadSignedUrlDto,
    VideosDownloadError: {} as ErrorType,

    //downloading files (pdf, images etc) to local directory
    isDownloadingWorkOrderFiles: false,
    isDownloadingWorkOrderFilesFailed: false,
    isDownloadingWorkOrderFilesSuccess: false,
    DownloadingWorkOrderFilesError: {} as ErrorType,

    //updating details (like title or description) of a workorder
    isUpdatingWorkOrderDetails: false,
    isUpdatingWorkOrderDetailsFailed: false,
    isUpdatingWorkOrderDetailsSuccess: false,
    UpdatingWorkOrderDetailsError: {} as ErrorType,

    //sending work order email
    isSendingWorkOrderEmail: false,
    isSendingWorkOrderEmailFailed: false,
    isSendingWorkOrderEmailSuccess: false,
    SendingWorkOrderEmailError: {} as ErrorType,

    //adding a workorder note
    isAddingWorkOrderNote: false,
    isAddingWorkOrderNoteFailed: false,
    isAddingWorkOrderNoteSuccess: false,
    AddingWorkOrderNoteError: {} as ErrorType,

    //Deleting a workorder note
    isDeletingWorkOrderNote: false,
    isDeletingWorkOrderNoteFailed: false,
    isDeletingWorkOrderNoteSuccess: false,
    DeletingWorkOrderNoteError: {} as ErrorType,

    //Exporting a work order
    isExportingWorkOrder: false,
    isExportingWorkOrderSuccess: false,
    isExportingWorkOrderFailed: false,
    ExportingWorkOrderError: {} as ErrorType,
  } as WorkOrderState;
};

const workOrderSlice = createSlice({
  name: 'workorders',
  initialState: getInitialState(),
  reducers: {
    setWorkOrderSearchFilters(state, action: PayloadAction<Partial<IWorkOrderSearchFilters>>) {
      state.workOrderSearchFilters = {
        ...state.workOrderSearchFilters,
        ...action.payload,
      };
    },
    getWorkOrders(state, action) {
      state.isFetchingWorkOrders = true;
      state.isFetchingWorkOrdersFailed = false;
      state.isFetchingWorkOrdersSuccess = false;
    },
    setWorkOrders(state, action) {
      state.isFetchingWorkOrders = false;
      state.isFetchingWorkOrdersSuccess = true;

      let _payload = action.payload as WorkOrderSearchResponseDtoV2;

      //get a copy of the current book
      let currentMaintenancePages = [...state.WorkOrdersDataPages];

      //if startindex = 0, just reset the book because that means it's loading for the first time or he reset the range size
      if (_payload.StartIndex === 0) currentMaintenancePages = [];

      //find out what page we are on
      let whatPageResultsIsThis = Math.ceil((_payload.StartIndex ?? 0) / (_payload?.RangeSize ?? 0));

      //now, add this new data page to our book
      currentMaintenancePages[whatPageResultsIsThis] = {
        Rows: _payload?.Rows,
      };

      //return the full
      state.WorkOrdersFetchResponseDto = action.payload;
      state.WorkOrdersDataPages = currentMaintenancePages;
      state.WorkOrdersFetchError = {} as ErrorType;
    },
    setWorkOrdersFetchError(state, action) {
      state.isFetchingWorkOrders = false;
      state.isFetchingWorkOrdersFailed = true;
      state.isFetchingWorkOrdersSuccess = false;
      state.WorkOrdersFetchError = action.payload;
    },
    createWorkOrder(state) {
      state.isCreatingWorkOrder = true;
      state.isCreatingWorkOrderFailed = false;
      state.isCreatingWorkOrderSuccess = false;
      state.CreateWorkOrderError = {} as ErrorType;
    },
    setCreateWorkOrder(state, action) {
      state.isCreatingWorkOrder = false;
      state.isCreatingWorkOrderFailed = false;
      state.isCreatingWorkOrderSuccess = true;
      state.workOrderDetailDto = action.payload;
    },
    setCreateWorkOrderError(state, action) {
      state.isCreatingWorkOrder = false;
      state.isCreatingWorkOrderFailed = true;
      state.isCreatingWorkOrderSuccess = false;
      state.CreateWorkOrderError = action.payload;
    },
    getWorkOrderByUuid(state, action) {
      state.isGettingWorkOrder = true;
      state.isGettingWorkOrderSuccess = false;
      state.isGettingWorkOrderfailed = false;
      state.workOrderDetailDto = {} as WorkOrderDto;
    },
    getWorkOrderById(state, action) {
      state.isGettingWorkOrder = true;
      state.isGettingWorkOrderSuccess = false;
      state.isGettingWorkOrderfailed = false;
      state.workOrderDetailDto = {} as WorkOrderDto;
    },
    setWorkOrder(state, action) {
      state.isGettingWorkOrder = false;
      state.isGettingWorkOrderSuccess = true;
      state.isGettingWorkOrderfailed = false;
      state.workOrderDetailDto = action.payload;
    },
    setWorkOrderError(state, action) {
      state.isGettingWorkOrder = false;
      state.isGettingWorkOrderSuccess = false;
      state.isGettingWorkOrderfailed = true;
    },
    getWorkOrderBase64Images(state) {
      state.ImagesDownloadError = {} as ErrorType;
      state.isFetchingBase64Images = true;
      state.isFetchingBase64ImagesFailed = false;
      state.isFetchingBase64ImagesSuccess = false;
    },
    setWorkOrderBase64Images(state, action) {
      state.isFetchingBase64Images = false;
      state.isFetchingBase64ImagesFailed = false;
      state.isFetchingBase64ImagesSuccess = true;
      state.WorkOrderImagesData = [...state.WorkOrderImagesData, action.payload];
    },
    setWorkOrderBase64ImagesError(state, action) {
      state.ImagesDownloadError = action.payload;
      state.isFetchingBase64Images = false;
      state.isFetchingBase64ImagesFailed = true;
      state.isFetchingBase64ImagesSuccess = false;
    },
    getWorkOrderVideos(state) {
      state.ImagesDownloadError = {} as ErrorType;
      state.isFetchingVideos = true;
      state.isFetchingVideosFailed = false;
      state.isFetchingVideosSuccess = false;
    },
    setWorkOrderVideos(state, action) {
      state.isFetchingVideos = false;
      state.isFetchingVideosFailed = false;
      state.isFetchingVideosSuccess = true;
      state.MaintenanceVideo = action.payload;
    },
    setWorkOrderVideosError(state, action) {
      state.VideosDownloadError = action.payload;
      state.isFetchingVideos = false;
      state.isFetchingVideosFailed = true;
      state.isFetchingVideosSuccess = false;
    },
    getUpdateWorkOrderDetails(state) {
      const newstate = {
        isUpdatingWorkOrderDetails: true,
        isUpdatingWorkOrderDetailsFailed: false,
        isUpdatingWorkOrderDetailsSuccess: false,
        UpdatingWorkOrderDetailsError: {} as ErrorType,
      };
      return { ...state, ...newstate };
    },
    setUpdateWorkOrderDetails(state, action) {
      const newstate = {
        isUpdatingWorkOrderDetails: false,
        isUpdatingWorkOrderDetailsFailed: false,
        isUpdatingWorkOrderDetailsSuccess: true,
        workOrderDetailDto: action.payload,
      };
      return { ...state, ...newstate };
    },
    setUpdateWorkOrderDetailsError(state, action) {
      const newstate = {
        isUpdatingWorkOrderDetails: false,
        isUpdatingWorkOrderDetailsFailed: true,
        isUpdatingWorkOrderDetailsSuccess: false,
        UpdatingWorkOrderDetailsError: action.payload,
      };
      return { ...state, ...newstate };
    },

    getSendWorkOrderEmail(state) {
      const newstate = {
        isSendingWorkOrderEmail: true,
        isSendingWorkOrderEmailFailed: false,
        isSendingWorkOrderEmailSuccess: false,
        SendingWorkOrderEmailError: {} as ErrorType,
      };
      return { ...state, ...newstate };
    },
    setSendWorkOrderEmail(state, action) {
      const newstate = {
        isSendingWorkOrderEmail: false,
        isSendingWorkOrderEmailFailed: false,
        isSendingWorkOrderEmailSuccess: true,
        workOrderDetailDto: action.payload,
      };
      return { ...state, ...newstate };
    },
    setSendWorkOrderEmailError(state, action) {
      const newstate = {
        isSendingWorkOrderEmail: false,
        isSendingWorkOrderEmailFailed: true,
        isSendingWorkOrderEmailSuccess: false,
        SendingWorkOrderEmailError: action.payload,
      };
      return { ...state, ...newstate };
    },
    getDownloadWorkOrderFile(state) {
      const newstate = {
        isDownloadingWorkOrderFiles: true,
        isDownloadingWorkOrderFilesFailed: false,
        isDownloadingWorkOrderFilesSuccess: false,
        DownloadingWorkOrderFilesError: {} as ErrorType,
      };
      return { ...state, ...newstate };
    },
    setDownloadWorkOrderFile(state) {
      const newstate = {
        isDownloadingWorkOrderFiles: false,
        isDownloadingWorkOrderFilesFailed: false,
        isDownloadingWorkOrderFilesSuccess: true,
      };
      return { ...state, ...newstate };
    },
    setDownloadWorkOrderFileError(state, action) {
      const newstate = {
        isDownloadingWorkOrderFiles: false,
        isDownloadingWorkOrderFilesFailed: true,
        isDownloadingWorkOrderFilesSuccess: false,
        DownloadingWorkOrderFilesError: action.payload,
      };
      return { ...state, ...newstate };
    },
    getAddWorkOrderNote(state) {
      const newstate = {
        isAddingWorkOrderNote: true,
        isAddingWorkOrderNoteFailed: false,
        isAddingWorkOrderNoteSuccess: false,
        AddingWorkOrderNoteError: {} as ErrorType,
      };
      return { ...state, ...newstate };
    },
    setAddWorkOrderNotes(state, action) {
      const newstate = {
        isAddingWorkOrderNote: false,
        isAddingWorkOrderNoteFailed: false,
        isAddingWorkOrderNoteSuccess: true,
        workOrderDetailDto: action.payload,
      };
      return { ...state, ...newstate };
    },
    setErrorAddWorkOrderNotes(state, action) {
      const newstate = {
        isAddingWorkOrderNote: false,
        isAddingWorkOrderNoteFailed: true,
        isAddingWorkOrderNoteSuccess: false,
        AddingWorkOrderNoteError: action.payload,
      };
      return { ...state, ...newstate };
    },
    getDeleteWorkOrderNote(state) {
      const newstate = {
        //Deleting a workorder note
        isDeletingWorkOrderNote: true,
        isDeletingWorkOrderNoteFailed: false,
        isDeletingWorkOrderNoteSuccess: false,
        DeletingWorkOrderNoteError: {} as ErrorType,
      };
      return { ...state, ...newstate };
    },
    setDeleteWorkOrderNote(state, action) {
      const newstate = {
        //Deleting a workorder note
        isDeletingWorkOrderNote: false,
        isDeletingWorkOrderNoteFailed: false,
        isDeletingWorkOrderNoteSuccess: true,
        workOrderDetailDto: action.payload,
      };
      return { ...state, ...newstate };
    },
    setErrorDeleteWorkOrderNote(state, action) {
      const newstate = {
        //Deleting a workorder note
        isDeletingWorkOrderNote: false,
        isDeletingWorkOrderNoteFailed: true,
        isDeletingWorkOrderNoteSuccess: false,
        DeletingWorkOrderNoteError: action.payload,
      };
      return { ...state, ...newstate };
    },
    resetWorkOrderState(state) {
      //not really used anywhere.
      return getInitialState();
    },
    resetAttachments(state) {
      const resetState = {
        isFetchingBase64Images: false,
        isFetchingBase64ImagesFailed: false,
        isFetchingBase64ImagesSuccess: false,
        ImagesDownloadError: {} as ErrorType,
        WorkOrderImagesData: [],
        isFetchingVideos: false,
        isFetchingVideosFailed: false,
        isFetchingVideosSuccess: false,
        MaintenanceVideo: {} as ReadSignedUrlDto,
        VideosDownloadError: {} as ErrorType,
      };

      return { ...state, ...resetState };
    },
    resetResponseDtos(state) {
      return { ...state };
    },
    getExportWorkOrder(state) {
      const newState = {
        isExportingWorkOrder: true,
        isExportingWorkOrderSuccess: false,
        isExportingWorkOrderFailed: false,
        ExportingWorkOrderError: {} as ErrorType,
      };
      return { ...state, ...newState };
    },
    setExportWorkOrder(state) {
      const newState = {
        isExportingWorkOrder: false,
        isExportingWorkOrderSuccess: true,
        isExportingWorkOrderFailed: false,
      };
      return { ...state, ...newState };
    },
    setErrorExportWorkOrder(state, action) {
      const newState = {
        isExportingWorkOrder: false,
        isExportingWorkOrderSuccess: false,
        isExportingWorkOrderFailed: true,
        ExportingWorkOrderError: action.payload,
      };
      return { ...state, ...newState };
    },
    resetCreateWorkOrderState(state) {
      state.isCreatingWorkOrder = false;
      state.isCreatingWorkOrderFailed = false;
      state.isCreatingWorkOrderSuccess = false;
      state.CreateWorkOrderError = {} as ErrorType;
    },
    clearWorkOrderDetailsState(state) {
      //clear everything out except the main search stuff.
      return {
        ...getInitialState(),
        workOrderSearchFilters: state.workOrderSearchFilters,
        WorkOrdersFetchResponseDto: state.WorkOrdersFetchResponseDto,
        WorkOrdersDataPages: state.WorkOrdersDataPages,
      };
    },
    resetWorkOrderSearchFilters(state) {
      state.workOrderSearchFilters = initialWorkOrderSearchFilters;
    },
  },
});
export const {
  setWorkOrderSearchFilters,
  getWorkOrders,
  setWorkOrders,
  setWorkOrdersFetchError,
  resetWorkOrderState,
  getWorkOrderByUuid,
  getWorkOrderById,
  setWorkOrder,
  setWorkOrderError,
  createWorkOrder,
  setCreateWorkOrder,
  setCreateWorkOrderError,
  getWorkOrderBase64Images,
  setWorkOrderBase64Images,
  setWorkOrderBase64ImagesError,
  setWorkOrderVideos,
  getWorkOrderVideos,
  setWorkOrderVideosError,
  resetAttachments,
  getUpdateWorkOrderDetails,
  setUpdateWorkOrderDetails,
  setUpdateWorkOrderDetailsError,
  getSendWorkOrderEmail,
  setSendWorkOrderEmail,
  setSendWorkOrderEmailError,
  getDownloadWorkOrderFile,
  setDownloadWorkOrderFile,
  setDownloadWorkOrderFileError,
  getAddWorkOrderNote,
  setAddWorkOrderNotes,
  setErrorAddWorkOrderNotes,
  setDeleteWorkOrderNote,
  setErrorDeleteWorkOrderNote,
  getDeleteWorkOrderNote,
  resetResponseDtos,
  getExportWorkOrder,
  setErrorExportWorkOrder,
  setExportWorkOrder,
  resetCreateWorkOrderState,
  clearWorkOrderDetailsState,
  resetWorkOrderSearchFilters,
} = workOrderSlice.actions;

export default workOrderSlice.reducer;
export const selectWorkOrderState = (state: RootState) => state.workOrderState;
export const selectWorkOrderSearchFitlers = (state: RootState) => state.workOrderState.workOrderSearchFilters;
export const selectWorkOrderHistoryCurrentPage = (state: RootState) => state.workOrderState.WorkOrdersDataPages;
